import { Button, Text, Flex, DialogBody } from '@chakra-ui/react';
import { useBreakpoint } from 'hooks/utils/layout/useBreakpoint';
import React, { ReactElement } from 'react';
import { Breakpoints } from 'types/breakpoints';
import { ModalShareVariantProps, SharedModalProps } from 'types/Modal';
import { useNovaToast } from 'hooks/utils/useNovaToast';
import { toastContent } from 'config/toast';
import { ToastVariant } from 'types/Toast';
import { InviteResponse, useInviteUser } from 'hooks/api/backend/user/useReferral';
import { EmailIcon } from 'assets/icons/v2';
import { NovaIcon } from 'components/ui/novaIcon';
import { Form, RHFFormInput } from 'components/common/Form/v2';
import { errorMessage } from 'components/common/Form/ErrorMessages';
import Joi from 'joi';
import { CustomModal } from '../CustomModal';

type Props = SharedModalProps & ModalShareVariantProps;
type MailInput = {
  email: string;
};

const schema = Joi.object({
  email: Joi.string()
    .email({
      tlds: {
        allow: false,
      },
    })
    .required()
    .messages({
      'string.empty': errorMessage.empty,
      'string.email': errorMessage.email,
    }),
});

export const ShareModal = ({ handleClose, isShown }: Props): ReactElement => {
  const isDesktop = useBreakpoint(Breakpoints.DESKTOP);
  const { mutateAsync: inviteUser, isPending } = useInviteUser();
  const { show: showToast } = useNovaToast();

  const handleSubmit = async (values: MailInput): Promise<void> => {
    const inviteStatus = await inviteUser(values.email);
    switch (inviteStatus) {
      case InviteResponse.INVITE_SUCCESS:
        showToast(toastContent.referral.inviteSuccess);
        handleClose();
        break;
      case InviteResponse.INVITE_ERROR:
        showToast(toastContent.referral.inviteError);
        break;

      default:
        showToast({ ...toastContent.error.default, variant: ToastVariant.ERROR });
        break;
    }
  };

  return (
    <CustomModal
      isOpen={isShown}
      onClose={handleClose}
      placement="center"
      size="md"
      hideHeader={!isDesktop}
    >
      <DialogBody p={{ base: '0', md: '0 40px 30px' }} display="flex" flexDirection="column">
        <Flex
          p={{ base: 'l', md: '0' }}
          textAlign={{ base: 'center', md: 'left' }}
          mb={{ md: '30px' }}
          flexDir="column"
          gap="15px"
        >
          <Text textStyle="h1.medium" color="text.primary">
            Gemeinsam zum Erfolg!
          </Text>
          <Text>
            Lade Freunde ein und helft euch gegenseitig - mit <b>20 %</b> Rabatt auf Novaheal für
            deine Freunde.
          </Text>
          <Form<MailInput, typeof schema>
            onSubmit={(values) => handleSubmit(values)}
            schema={schema}
          >
            <Flex flexDirection="column" gap="4xl" alignItems="center" pt="5px" width="100%">
              <RHFFormInput
                type="email"
                name="email"
                leftElement={<NovaIcon icon={EmailIcon} size="m" aria-hidden />}
                placeholder="Mail"
              />
              <Button
                type="submit"
                variant="primary"
                size="l"
                w={{ base: 'full', md: '220px' }}
                loading={isPending}
              >
                Freund:in einladen
              </Button>
            </Flex>
          </Form>
        </Flex>
      </DialogBody>
    </CustomModal>
  );
};
